<script lang="ts">
// Packages
import { defineComponent, type PropType } from 'vue';
import algoliasearch from 'algoliasearch/lite';
import { history as historyRouter } from 'instantsearch.js/es/lib/routers';

// Modules
import { fetchGeoData } from '@modules/google-maps';

// Directives
import dropdown, { closeDropdown } from '@vue-directives/dropdown';

// Styles (for dev)
// import 'instantsearch.css/themes/reset.css';
// import 'instantsearch.css/themes/satellite.css';

// Components
import {
  AisClearRefinements,
  AisConfigure,
  AisCurrentRefinements,
  AisHits,
  AisInstantSearch,
  AisPagination,
  AisSearchBox,
  AisSortBy,
  AisStats,
  AisStateResults,
} from 'vue-instantsearch/vue3/es';
import RefinementPanel from '@vue-components/RefinementPanel.vue';
import RightArrowThick from '@vue-components/svgs/RightArrowThick.vue';

type RefinementList = { [key: string]: string[] };
type SortOption = {
  value: string;
  label: string;
};

type UiState = {
  [indexName: string]: {
    refinementList?: RefinementList;
    page?: number;
    query?: string;
    sortBy?: string;
  };
};
type UiRoute = Record<string, string /* | string[] | number */>;

const router = historyRouter<UiRoute>();

type Refinement = {
  label: string;
  attribute: string;
};

export default defineComponent({
  components: {
    AisClearRefinements,
    AisConfigure,
    AisCurrentRefinements,
    AisHits,
    AisInstantSearch,
    AisPagination,
    AisSearchBox,
    AisSortBy,
    AisStats,
    AisStateResults,
    RefinementPanel,
    RightArrowThick,
  },
  directives: {
    dropdown,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    indexName: {
      type: String,
      required: true,
    },
    refinements: {
      type: Array as PropType<Refinement[]>,
      required: true,
    },
    sortOptions: {
      type: Array as PropType<SortOption[]>,
      required: false,
      default: () => [],
    },
    heading: {
      type: String,
      required: false,
      default: 'Filter by',
    },
    headingElement: {
      type: String,
      required: false,
      default: 'h2',
    },
  },
  data() {
    return {
      searchClient: algoliasearch(this.appId, this.searchApiKey),
      userLat: null as number | null,
      userLng: null as number | null,
      locationQuery: null as string | null,
      useCurrentLocation: false,
      hasLocationError: false,
      sortIndex: this.sortOptions[0]?.value ?? this.indexName,
    };
  },
  computed: {
    defaultSortIndex() {
      return this.sortOptions[0]?.value ?? this.indexName;
    },
    routing() {
      const { indexName, defaultSortIndex, refinements, sortOptions } = this;
      return {
        router,
        stateMapping: {
          stateToRoute(uiState: UiState) {
            const indexUiState = uiState[indexName];
            const route: UiRoute = {};
            const { refinementList, page, query, sortBy } = indexUiState;
            if (refinementList) {
              Object.keys(refinementList).forEach((key) => {
                if (refinementList[key]) {
                  route[key] = (refinementList[key] as Array<string>).join('~');
                }
              });
            }
            if (query) {
              route.q = query;
            }
            if (page && page > 1) {
              route.p = page.toString();
            }
            if ('sortBy' in indexUiState) {
              // Remove index name prefix. sortBy is undefined when using the primary index (distance)
              route.s = sortBy ? sortBy.slice(indexName.length + 1) : 'distance';
            }
            return route;
          },
          routeToState(routeState: UiRoute): UiState {
            // The 'distance' sort option is actually the main index with search params applied
            const prefixedSortBy = routeState.s
              ? routeState.s === 'distance'
                ? indexName
                : `${indexName}_${routeState.s}`
              : null;
            const sortBy =
              prefixedSortBy &&
              sortOptions.find((sortOption) => sortOption.value === prefixedSortBy)
                ? prefixedSortBy
                : null;

            return {
              [indexName]: {
                query: routeState.q,
                refinementList: Object.fromEntries(
                  refinements
                    .map(({ attribute }) => [
                      attribute,
                      routeState[attribute] ? routeState[attribute].split('~') : undefined,
                    ])
                    .filter(([, val]) => val !== undefined),
                ),
                page: Math.min(Number(routeState.p) || 1, 1),
                sortBy: sortBy ?? defaultSortIndex,
              },
            };
          },
        },
      };
    },
    hasLocation() {
      return this.userLat !== null && this.userLng !== null;
    },
  },
  watch: {
    /**
     * If sorting by distance, get location TODO
     */
    sortIndex(sortIndex) {
      if (sortIndex !== this.indexName) return;

      // if (!hasLocation) {

      //   //  getCurrentPosition()
      // }
    },
  },
  methods: {
    closeDropdown,
    onPageChange() {
      this.$el.scrollIntoView();
    },
    async sortByLocation() {
      if (this.useCurrentLocation) {
        this.sortIndex = this.indexName; // Distance
        return;
      }

      // required attribute should handle this
      if (!this.locationQuery) {
        throw new Error('Missing location query');
      }

      this.setLocationCustomValidity(''); // reset

      // Geocode lookup
      const res = await fetchGeoData(this.locationQuery).catch((error) => {
        this.setLocationCustomValidity("Your location wasn't recognised");
        throw error;
      });

      if (res) {
        this.setCoordinates(res[0], res[1]);
        this.sortIndex = this.indexName; // Distance
      } else {
        this.setCoordinates(null, null);
        this.setLocationCustomValidity("Your location wasn't recognised");
      }
    },
    setLocationCustomValidity(error: string) {
      const locationQueryInput = (this.$refs as { locationQueryInput: HTMLInputElement })
        .locationQueryInput;
      locationQueryInput.setCustomValidity(error);

      if (error) {
        locationQueryInput.reportValidity();
      }
    },
    setCoordinates(lat: number | null, lng: number | null) {
      this.userLat = lat;
      this.userLng = lng;
    },
    getCurrentPosition() {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.useCurrentLocation = true;
          this.locationQuery = null;
          this.hasLocationError = false;
          this.setLocationCustomValidity(''); // reset

          this.setCoordinates(position.coords.latitude, position.coords.longitude);
        },
        () => {
          this.locationQuery = null;
          this.hasLocationError = true;
          this.useCurrentLocation = true;
          this.setLocationCustomValidity('Your location could not be determined');
          this.setCoordinates(null, null);
        },
      );
    },
  },
});
</script>
<template>
  <ais-instant-search
    :search-client="searchClient"
    :routing="routing"
    :index-name="indexName"
    :class-names="{
      'ais-InstantSearch':
        'bg-menzies-grey-300 pt-7.5 pb-10.5 text-menzies-blue-900 md:pt-24 md:pb-12',
    }"
  >
    <ais-configure
      v-if="sortIndex === indexName && hasLocation"
      :around-lat-lng.camel="`${userLat},${userLng}`"
      :around-radius.camel="'all'"
    />
    <ais-configure :index="sortIndex" />

    <div class="container">
      <!-- ====== START REFINEMENTS ====== -->
      <div class="mb-6 md:mb-11.5">
        <component
          :is="headingElement"
          class="mb-6 text-center text-4.5xl/12 font-bold text-menzies-blue-800 md:mb-9"
        >
          {{ heading }}
        </component>

        <div class="mx-auto max-w-169">
          <!-- ====== START SEARCH BOX ====== -->
          <ais-search-box
            class="mb-3 md:mb-9"
            :class-names="{
              'ais-SearchBox-form':
                'flex rounded-3xl border-2 border-[#d9d9d9] bg-white px-6 py-1 text-base/7.5 focus-within:[outline:auto] focus-within:outline-offset-4',
              'ais-SearchBox-input':
                'flex-grow appearance-none text-menzies-blue-900 outline-none focus:ring-0 focus:ring-offset-0',
              'ais-SearchBox-submit': 'ml-4',
              'ais-SearchBox-reset': 'hidden',
            }"
            placeholder="Search..."
          >
            <template #submit-icon>
              <svg
                width="20"
                height="19"
                viewBox="0 0 20 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle
                  cx="8.60864"
                  cy="8.60868"
                  r="7.60487"
                  transform="rotate(-0.0250705 8.60864 8.60868)"
                  stroke="#0A4F67"
                  stroke-width="2"
                />
                <line
                  x1="14.642"
                  y1="13.3573"
                  x2="18.7396"
                  y2="17.4549"
                  stroke="#0A4F67"
                  stroke-width="2"
                />
              </svg>
            </template>
          </ais-search-box>
          <!-- ====== END SEARCH BOX ====== -->

          <!-- ====== START GEOCODE ====== -->
          <form
            class="mb-9 flex flex-col gap-x-5 gap-y-3 md:flex-row md:items-center"
            action=""
            @submit.prevent="sortByLocation"
          >
            <div class="relative flex-grow">
              <input
                ref="locationQueryInput"
                v-model="locationQuery"
                type="search"
                autocorrect="on"
                autocapitalize="none"
                maxlength="512"
                aria-label="Enter place name or postcode"
                :placeholder="
                  useCurrentLocation ? 'Current location' : 'Enter place name or postcode'
                "
                :required="!useCurrentLocation"
                class="w-full rounded-3xl border-2 border-[#d9d9d9] bg-white py-1 pr-6 pl-10.5 text-base/7.5 text-menzies-blue-900 [&.has-error]:border-menzies-red"
                :class="{
                  'has-error': hasLocationError,
                }"
                @input="
                  ((useCurrentLocation = false),
                  (hasLocationError = false),
                  setCoordinates(null, null),
                  setLocationCustomValidity(''))
                "
                @valid="hasLocationError = false"
                @invalid="hasLocationError = true"
                @blur="
                  (e) => !(e.target as HTMLInputElement).value.length && (hasLocationError = false)
                "
              />
              <!-- @vue-expect-error navigator is added with vueInstance.config.globalProperties -->
              <button
                v-if="'geolocation' in navigator"
                type="button"
                class="absolute top-0 left-0 grid h-full cursor-pointer place-items-center pr-2.5 pl-4"
                title="Use your current location"
                @click="getCurrentPosition"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M14.3693 0.768949C14.3776 0.777313 14.3832 0.787864 14.3859 0.799333C14.3886 0.810774 14.3881 0.822649 14.3845 0.833859L9.75791 14.7094V14.7108C9.7538 14.7229 9.74614 14.7337 9.73582 14.7412C9.72529 14.7488 9.71219 14.7529 9.69922 14.753C9.68627 14.753 9.67311 14.7488 9.66262 14.7412C9.65231 14.7337 9.6446 14.7229 9.64052 14.7108V14.7094L7.56478 8.55541V8.55403C7.48777 8.32621 7.35914 8.1192 7.18913 7.94912C7.01895 7.77894 6.81154 7.64982 6.58353 7.57278L6.34322 8.28403L6.58284 7.57347L0.4288 5.49773L0.426729 5.49704C0.414641 5.49289 0.403804 5.4853 0.396345 5.47494C0.388888 5.46456 0.38538 5.45182 0.385297 5.43903C0.385312 5.42607 0.389428 5.41297 0.397036 5.40243C0.404606 5.39199 0.415206 5.38444 0.427419 5.38034L0.42811 5.37964L14.3037 0.753067C14.3149 0.749413 14.3268 0.749036 14.3382 0.751686C14.3498 0.754428 14.3609 0.760543 14.3693 0.768949Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>
              </button>
            </div>
            <input
              type="submit"
              class="btn btn-dark-blue flex min-w-55 items-center px-4.5 py-1 text-base/7.5"
              value="Update results"
            />
          </form>
        </div>
        <!-- ====== END GEOCODE ====== -->

        <div
          class="mb-22.5 auto-rows-auto justify-center gap-x-6.5 gap-y-5 md:mb-24 md:flex md:flex-wrap md:items-center"
        >
          <refinement-panel
            v-for="refinement in refinements"
            :key="refinement.attribute"
            class="basis-50"
            v-bind="refinement"
          />
        </div>

        <ais-state-results>
          <template #default="{ state, results: { hits } }">
            <div
              class="flex flex-wrap items-center justify-center gap-4 text-base/7.5 md:flex-nowrap md:items-start md:gap-x-8"
            >
              <!-- ====== START SORT BY ====== -->
              <ais-sort-by
                v-if="sortOptions.length"
                v-dropdown
                :items="sortOptions"
                class="dropdown group relative"
              >
                <template #default="{ items, currentRefinement, refine }">
                  <button
                    aria-expanded="false"
                    class="dropdown-trigger btn btn-dark-blue flex items-center bg-[#141446] px-4.5 py-1 text-base/7.5 whitespace-nowrap"
                    type="button"
                  >
                    Sort by
                    <svg
                      class="ml-2.5"
                      viewBox="0 0 16 16"
                      height="16"
                      width="16"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M2 5L8.16086 10.6869C8.35239 10.8637 8.64761 10.8637 8.83914 10.6869L15 5"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                      />
                    </svg>
                  </button>
                  <ul
                    class="shadow-default absolute top-full left-0 z-[1] mt-1.5 ml-4.5 hidden w-max max-w-176 min-w-60 space-y-1 bg-white py-2 group-[.open]:block"
                  >
                    <li
                      v-for="item in items"
                      :key="item.value"
                      :aria-current="currentRefinement === item.value"
                    >
                      <button
                        ref="sortButtons"
                        :data-sort="item.value"
                        class="block w-full cursor-pointer px-4 text-left text-base/7.5 text-menzies-blue-900 transition-colors duration-300 hover:bg-menzies-neutral-100"
                        :class="{
                          'bg-menzies-yellow-300 hover:bg-menzies-yellow-300':
                            currentRefinement === item.value,
                        }"
                        type="button"
                        @click="
                          ((sortIndex = item.value),
                          refine(item.value),
                          closeDropdown(($event.target as HTMLElement).closest('.dropdown')!))
                        "
                      >
                        {{ item.label }}
                      </button>
                    </li>
                  </ul>
                </template>
              </ais-sort-by>
              <!-- ====== END SORT BY ====== -->

              <!-- ====== START CURRENT REFINEMENTS ====== -->
              <ais-current-refinements class="mr-auto max-md:order-4 max-md:basis-full">
                <template #default="{ items /* , createURL */ }">
                  <ul class="flex flex-wrap justify-center gap-x-6 gap-y-2 md:gap-y-6">
                    <template v-for="item in items" :key="item.attribute">
                      <li
                        v-for="refinement in item.refinements"
                        :key="
                          [
                            refinement.attribute,
                            refinement.type,
                            refinement.value,
                            refinement.operator,
                          ].join(':')
                        "
                        class="flex items-center rounded-full border border-menzies-blue-800 bg-menzies-blue-800 px-4.5 py-1 text-base/7.5 text-white"
                      >
                        {{ refinement.label }}
                        <button
                          class="ml-1.5 grid size-7 cursor-pointer place-items-center"
                          title="Remove this refinement"
                          type="button"
                          @click.prevent="item.refine(refinement)"
                        >
                          <svg height="14" width="14" viewBox="0 0 13 13" aria-hidden="true">
                            <line
                              x1="0.6"
                              y1="0.6"
                              x2="12.4"
                              y2="12.4"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                            />
                            <line
                              x1="0.6"
                              y1="12.4"
                              x2="12.4"
                              y2="0.6"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                            />
                          </svg>
                        </button>
                      </li>
                    </template>
                  </ul>
                </template>
              </ais-current-refinements>
              <!-- ====== END CURRENT REFINEMENTS ====== -->

              <!-- ====== START CLEAR REFINEMENTS ====== -->
              <!--
                By default this component only resets refinements, which doesn't include the current
                search option. We extend it here to reset the sort and also the geocoding fields
              -->
              <ais-clear-refinements class="text-center font-bold max-md:order-3">
                <template #default="{ canRefine, refine }">
                  <button
                    type="reset"
                    class="cursor-pointer"
                    :class="{
                      hidden: !(canRefine || state.index !== defaultSortIndex),
                    }"
                    :disabled="!(canRefine || state.index !== defaultSortIndex)"
                    @click.prevent="
                      (canRefine && refine(),
                      (locationQuery = null),
                      (useCurrentLocation = false),
                      setCoordinates(null, null),
                      sortOptions.length && (sortIndex = defaultSortIndex!))
                    "
                  >
                    Clear filters
                  </button>
                </template>
              </ais-clear-refinements>
              <!-- ====== END CLEAR REFINEMENTS ====== -->

              <ais-stats class="text-center md:text-left">
                <template #default="{ nbHits }">
                  Showing {{ hits.length }} of {{ nbHits }} results
                </template>
              </ais-stats>
            </div>
          </template>
        </ais-state-results>
      </div>
      <!-- ====== END REFINEMENTS ====== -->

      <!-- ====== START HITS ====== -->
      <ais-hits
        :class-names="{
          'ais-Hits': 'mb-11.5',
          'ais-Hits-list':
            'grid-cols grid grid-cols-[repeat(auto-fill,minmax(min(22.5rem,_100%),_1fr))] gap-x-11.5 gap-y-9.5 md:gap-y-11.5',
        }"
      >
        <template #item="props">
          <slot name="item" v-bind="props" />
        </template>
      </ais-hits>
      <!-- ====== END HITS ====== -->

      <!-- ====== START PAGINATION ====== -->
      <ais-pagination @page-change="onPageChange">
        <template
          #default="{
            currentRefinement,
            nbPages,
            pages,
            isFirstPage,
            isLastPage,
            refine,
            createURL,
          }"
        >
          <ul
            class="flex items-center justify-center gap-x-2 text-xl/normal font-semibold text-menzies-blue-900"
          >
            <li class="mr-1">
              <span
                v-if="isFirstPage"
                class="grid size-10 cursor-not-allowed place-items-center rounded-full border-2 border-menzies-blue-900 opacity-60"
                aria-label="Previous Page"
              >
                <right-arrow-thick class="rotate-180" aria-hidden="true" width="14" height="11" />
              </span>
              <a
                v-else
                class="grid size-10 place-items-center rounded-full border-2 border-menzies-blue-900 transition-colors duration-300 hover:bg-menzies-blue-900 hover:text-white"
                aria-label="Previous Page"
                :href="createURL(currentRefinement - 1)"
                @click.exact.left.prevent="refine(currentRefinement - 1)"
              >
                <right-arrow-thick class="rotate-180" aria-hidden="true" width="14" height="11" />
              </a>
            </li>
            <template v-if="currentRefinement > 2">
              <li>
                <a :href="createURL(0)" class="block w-8 text-center" @click.prevent="refine(0)">
                  1
                </a>
              </li>
              <li v-if="currentRefinement > 3" class="block w-8 text-center">. . .</li>
            </template>
            <li
              v-for="page in pages.filter((num: number) => Math.abs(num - currentRefinement) <= 2)"
              :key="page"
            >
              <a
                :href="createURL(page)"
                class="block w-8 text-center"
                :class="
                  page === currentRefinement
                    ? 'rounded-full bg-menzies-blue-900 text-white hover:text-white!'
                    : ''
                "
                @click.prevent="refine(page)"
              >
                {{ page + 1 }}
              </a>
            </li>
            <template v-if="nbPages - currentRefinement > 3">
              <li v-if="nbPages - currentRefinement > 4" class="block w-8 text-center">. . .</li>
              <li>
                <a
                  :href="createURL(nbPages)"
                  class="block w-8 text-center"
                  @click.prevent="refine(nbPages)"
                >
                  {{ nbPages }}
                </a>
              </li>
            </template>
            <li class="ml-1">
              <span
                v-if="isLastPage"
                class="grid size-10 cursor-not-allowed place-items-center rounded-full border-2 border-menzies-blue-900 opacity-60"
                aria-label="Next Page"
              >
                <right-arrow-thick aria-hidden="true" width="14" height="11" />
              </span>
              <a
                v-else
                class="grid size-10 place-items-center rounded-full border-2 border-menzies-blue-900 transition-colors duration-300 hover:bg-menzies-blue-900 hover:text-white"
                aria-label="Next Page"
                :href="createURL(currentRefinement + 1)"
                @click.exact.left.prevent="refine(currentRefinement + 1)"
              >
                <right-arrow-thick aria-hidden="true" width="14" height="11" />
              </a>
            </li>
          </ul>
        </template>
      </ais-pagination>
      <!-- ====== END PAGINATION ====== -->
    </div>
  </ais-instant-search>
</template>
